import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import get from 'lodash/get';
import { Grid, Button, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import WarnBeforeDeleting from '../../common/warn-before-deleting';
import { CreateTargetDeployment } from './create-target-deployment';
import { shortDateTimeFormat } from '../../../lib/utils';
import { tableOptions } from '../../../lib/table-utils';
import { EditTargetDeploymentModal } from './edit-target-deployment-modal';
import CustomToolbar from '../custom-toolbar';
import targetDeploymentActions from '../../../actions/targetDeployments';
import * as snackbarActions from '../../../actions/snackbar';

const styles = {
  pageContainer: {
    height: '100%',
  },
  floatRight: {
    float: 'right',
  },
};

function getTargetDeploymentColumns(onDeleteClick) {
  return [
    { name: 'Id', options: { display: 'excluded' } },
    {
      name: 'Effective Date',
      options: {
        sortOrder: 'asc',
        customBodyRender: (data) => {
          if (data) {
            return moment(data).format(shortDateTimeFormat);
          } else {
            return '';
          }
        },
      },
    },
    { name: 'Quantity' },
    { name: 'Fleet' },
    {
      name: 'Delete',
      options: {
        customBodyRender: (data) => (
          <IconButton onClick={() => onDeleteClick(data)}>
            <DeleteIcon style={{ color: 'red' }} />
          </IconButton>
        ),
      },
    },
  ];
}

function getTargetDeploymentData(tz, targetDeployments, activeMarkets) {
  const data = targetDeployments.map((target) => {
    const targetMarketId = get(target, 'marketId', '');
    let fleetName = '';
    if (targetMarketId) {
      const fleet = activeMarkets.find((activeMarket) => activeMarket._id === targetMarketId);
      fleetName = fleet.name;
    }
    const tableData = [
      target._id,
      moment.tz(target.effectiveOn, tz),
      target.target || '',
      fleetName,
      target._id,
    ];
    return tableData;
  });
  return data;
}

function getTargetDeploymentTableOptions(addClickHandler, editClickHandler) {
  const basicOptions = { ...tableOptions };
  basicOptions.customToolbar = () => (
    <CustomToolbar
      onClick={addClickHandler}
      type="Add"
    />
  );
  basicOptions.onRowClick = (rowData, rowMeta, e) => {
    e.preventDefault();
    editClickHandler(rowData);
  };
  return basicOptions;
}

function TargetDeploymentPage({
  handleBackClick,
  market,
  markets,
  targetDeployments,
  deleteTargetDeployment,
  fetchTargetDeployments,
  updateTargetDeployment,
  showSuccessSnackbar,
  showErrorSnackbar,
}) {
  if (targetDeployments === undefined) return <div>Loading</div>;

  const [showCreateTargetDeployment, setShowCreateTargetDeployment] = useState(false);
  const [showEditTargetDeployment, setShowEditTargetDeployment] = useState(false);
  const [warnBeforeDeleting, setWarnBeforeDeleting] = useState(false);
  const [targetDeploymentId, setTargetDeploymentId] = useState(null);

  const currentTargetDeployment = targetDeployments.find(
    (targetDeployment) => targetDeployment._id === targetDeploymentId,
  );

  const optionsAddClickHandler = () => {
    setTargetDeploymentId(null);
    setShowCreateTargetDeployment(true);
  };
  const optionsEditClickHandler = (e) => {
    const id = get(e, '[0]');
    setTargetDeploymentId(id);
    setShowEditTargetDeployment(true);
  };

  const tableData = getTargetDeploymentData(market.tz, targetDeployments, markets);
  const dataTableOptions = getTargetDeploymentTableOptions(
    optionsAddClickHandler,
    optionsEditClickHandler,
  );
  const tableColumns = getTargetDeploymentColumns((data) => {
    setTargetDeploymentId(data);
    setWarnBeforeDeleting(true);
  });

  return (
    <Grid
      container
      style={styles.pageContainer}
    >
      <Grid
        item
        xs={12}
      >
        <Button
          onClick={handleBackClick}
          style={styles.floatRight}
        >
          Back
        </Button>
      </Grid>
      <Grid
        item
        xs={2}
      >
        &nbsp;
      </Grid>
      <Grid
        item
        xs={12}
      >
        <MUIDataTable
          title="Target Deployments"
          columns={tableColumns}
          data={tableData}
          options={dataTableOptions}
        />
      </Grid>
      <Grid
        item
        xs={12}
      >
        {currentTargetDeployment && !warnBeforeDeleting && (
          <EditTargetDeploymentModal
            market={market}
            currentTargetDeployment={currentTargetDeployment}
            open={showEditTargetDeployment && !warnBeforeDeleting}
            handleSave={(currentId, targetValue, effectiveOn) => {
              updateTargetDeployment(currentId, targetValue, effectiveOn)
                .then(() => {
                  fetchTargetDeployments(market._id);
                  setShowEditTargetDeployment(false);
                  showSuccessSnackbar(`Saved new target ${targetValue}`);
                })
                .catch((e) => showErrorSnackbar('Error saving deployment', e));
            }}
            handleClose={() => setShowEditTargetDeployment(false)}
          />
        )}
        {showCreateTargetDeployment && !warnBeforeDeleting && (
          <CreateTargetDeployment
            market={market}
            currentTargets={targetDeployments}
            markets={markets}
            open={showCreateTargetDeployment && !warnBeforeDeleting}
            handleSave={() => setShowCreateTargetDeployment(false)}
            handleClose={() => setShowCreateTargetDeployment(false)}
          />
        )}
        <WarnBeforeDeleting
          isOpen={warnBeforeDeleting}
          message="Are you sure you want to delete this target deployment?"
          onCancel={() => {
            setTargetDeploymentId(null);
            setWarnBeforeDeleting(false);
          }}
          onConfirm={() => {
            deleteTargetDeployment(targetDeploymentId)
              .then(() => {
                fetchTargetDeployments(market._id);
                showSuccessSnackbar('Successfully deleted target');
                setTargetDeploymentId(null);
                setWarnBeforeDeleting(false);
              })
              .catch((e) => {
                showErrorSnackbar('Error deleting deployment', e);
              });
          }}
        />
      </Grid>
    </Grid>
  );
}
function mapStateToProps({ markets, targetDeployments }) {
  return {
    market: markets.selectedMarket,
    markets: markets.activeMarkets,
    targetDeployments: targetDeployments.targetDeployments,
    targetDeploymentsLoading: targetDeployments.isLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      ...targetDeploymentActions,
      ...snackbarActions,
    },
    dispatch,
  );
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TargetDeploymentPage),
);
